// assets/custom-video-gallery.js

(function () {
  'use strict';

  const SELECTORS = {
    root:         '[data-component="video-gallery"]',
    track:        '[data-gallery-track]',
    prev:         '[data-gallery-prev]',
    next:         '[data-gallery-next]',
    tile:         '[data-tile]',
    thumb:        '[data-thumb]',
    inlineVideo:  '[data-inline-video]',
    dot:          '[data-dot]',
    modal:        '[data-modal]',
    modalClose:   '[data-modal-close]',
    modalInner:   '[data-modal-inner]',
    modalContent: '[data-modal-content]',
  };

  const instances = new Map();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function debounce(fn, delay) {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), delay); };
  }

  // ── Gallery class ───────────────────────────────────────────────────────────

  class VideoGallery {
    constructor(el) {
      this.el             = el;
      this.sectionId      = el.dataset.sectionId;
      this.displayMode    = el.dataset.display;
      this.playbackMode   = el.dataset.playback;
      this.pauseForMotion = el.dataset.reducedMotionPause === 'true';
      this.hoverAutoplay  = el.dataset.hoverAutoplay === 'true';
      this.autoplayCentered = el.dataset.autoplayCentered === 'true';
      this.track          = el.querySelector(SELECTORS.track);
      this.modal          = document.querySelector(`${SELECTORS.modal}[data-section-id="${this.sectionId}"]`);
      this._observers     = [];
      this._activeInlineTile = null;
      this._activeTile    = null;
      this._prevFocus     = null;
      this._focusTrapHandler = null;
      this._escHandler    = null;
      this._scrollHandler = null;

      this._init();
    }

    _init() {
      this._bindTiles();
      this._bindArrows();
      this._bindDots();
      this._bindDrag();
      this._bindModal();
      this._observeAutoplay();
      this._trackActiveTile();
    }

    // ── Active tile tracking (scroll position → dots + scale) ──────────────────

    _trackActiveTile() {
      const first = this.el.querySelector(SELECTORS.tile);
      if (first) this._setActiveTile(first);

      if (!this.track || this.displayMode !== 'scroll') return;

      this._updateGrabbable();

      this._scrollHandler = debounce(() => {
        const trackRect   = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closest = null;
        let minDist = Infinity;

        this.el.querySelectorAll(SELECTORS.tile).forEach(tile => {
          const r    = tile.getBoundingClientRect();
          const dist = Math.abs(r.left + r.width / 2 - trackCenter);
          if (dist < minDist) { minDist = dist; closest = tile; }
        });

        if (closest && closest !== this._activeTile) {
          this._setActiveTile(closest);
          this._maybeAutoplayCentered(closest);
        }
        this._updateGrabbable();
      }, 80);

      this.track.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    // ── Mouse drag-to-scroll (desktop) ─────────────────────────────────────────
    // Browsers only scroll an overflow container via touch/trackpad/wheel — a mouse
    // drag does nothing natively. Touch is left to the native scroller.
    _updateGrabbable() {
      if (!this.track) return;
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
      this.track.classList.toggle('is-grabbable', overflowing);
    }

    _bindDrag() {
      if (!this.track || this.displayMode !== 'scroll') return;
      let isDown = false, startX = 0, startScroll = 0, moved = false;
      const THRESHOLD = 4;

      // NOTE: no setPointerCapture — capturing retargets the trailing click to the
      // track, so tile taps (play/pause/modal) never reach the tile. Track the drag
      // with window listeners so clicks stay on their real targets.
      const onMove = (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > THRESHOLD) moved = true;
        if (moved) { e.preventDefault(); this.track.scrollLeft = startScroll - dx; }
      };
      const onUp = () => {
        if (!isDown) return;
        isDown = false;
        this.track.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      const onDown = (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return; // touch = native scroll
        if (e.button !== 0) return;                              // left button only
        isDown = true; moved = false;
        startX = e.clientX; startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      };
      // Swallow the post-drag click so dragging a tile doesn't open the modal / play it.
      const onClick = (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      };
      const onDragStart = (e) => e.preventDefault();

      this.track.addEventListener('pointerdown', onDown);
      this.track.addEventListener('click', onClick, true);
      this.track.addEventListener('dragstart', onDragStart);
      this._drag = { onDown, onMove, onUp, onClick, onDragStart };
    }

    _setActiveTile(tile) {
      this._activeTile = tile;
      const tiles = Array.from(this.el.querySelectorAll(SELECTORS.tile));
      const idx   = tiles.indexOf(tile);

      tiles.forEach(t => t.classList.toggle('is-active', t === tile));
      this.el.querySelectorAll(SELECTORS.dot).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === idx);
      });
    }

    // Auto-play the tile that has settled into the center of the strip (scroll/drag).
    // Native videos only; muted autoplay is required by browsers, so this relies on
    // the inline <video> being muted. Non-native (YouTube/Vimeo) tiles just pause inline.
    _maybeAutoplayCentered(tile) {
      if (!this.autoplayCentered || this.displayMode !== 'scroll' || !tile) return;
      if (prefersReducedMotion && this.pauseForMotion) return;
      const isNative = tile.dataset.videoType === 'hosted' || tile.dataset.videoType === 'shopify';
      if (isNative) {
        this._playInline(tile);
      } else {
        this._pauseAllInline();
      }
    }

    // ── Tile binding ───────────────────────────────────────────────────────────

    _bindTiles() {
      this.el.querySelectorAll(SELECTORS.tile).forEach(tile => {
        const isNative = tile.dataset.videoType === 'hosted' || tile.dataset.videoType === 'shopify';

        // Desktop hover — mouseenter only (never fires on touch devices).
        // Uses _playInlineHover which shows native controls for desktop UX.
        if (this.hoverAutoplay && isNative) {
          tile.addEventListener('mouseenter', () => {
            this._setActiveTile(tile);
            if (!prefersReducedMotion || !this.pauseForMotion) {
              this._playInlineHover(tile);
            }
          });
        }

        // Click / tap handler
        tile.addEventListener('click', () => {
          const iv    = tile.querySelector(SELECTORS.inlineVideo);
          const video = iv?.querySelector('video');

          // Inline video already visible → toggle play / pause.
          // Restore thumb on pause so the play-button overlay reappears.
          if (iv && !iv.hidden && video) {
            if (video.paused) {
              video.play().catch(() => {});
              tile.querySelector(SELECTORS.thumb)?.style.setProperty('opacity', '0');
            } else {
              video.pause();
              // Show thumbnail + play-button so user knows they can tap again
              tile.querySelector(SELECTORS.thumb)?.style.removeProperty('opacity');
            }
            return;
          }

          // First tap on a native tile.
          // Use _playInline (NO video.controls) so iOS does not render the native
          // player UI in paused state before play() runs — that blocks the play call.
          // With hover-autoplay OR centered-autoplay on, a tap controls inline playback
          // (play/pause) instead of opening the modal.
          if ((this.hoverAutoplay || this.autoplayCentered) && isNative) {
            this._setActiveTile(tile);
            this._playInline(tile);
            return;
          }

          this._onTileClick(tile);
        });
      });
    }

    _onTileClick(tile) {
      const type     = tile.dataset.videoType;
      const mode     = this.playbackMode;
      const isNative = type === 'hosted' || type === 'shopify';

      if (mode === 'inline') {
        isNative ? this._playInline(tile) : this._openModal(tile);
      } else if (mode === 'inline-unmute') {
        const iv = tile.querySelector(SELECTORS.inlineVideo);
        if (iv && !iv.hidden) { this._openModal(tile); }
        else if (isNative)    { this._playInline(tile); }
        else                  { this._openModal(tile); }
      } else {
        this._openModal(tile);
      }
    }

    // ── Inline playback ────────────────────────────────────────────────────────

    // Plain inline play — no native controls overlay.
    // Used for: click/tap (all platforms), dots, arrows, intersection-observer autoplay.
    // NOT setting video.controls means iOS will not render the native paused-UI before
    // play() executes, so the programmatic play() within a user gesture is honoured.
    _playInline(tile) {
      if (this._activeInlineTile === tile) return;
      this._pauseAllInline();
      const iv = tile.querySelector(SELECTORS.inlineVideo);
      if (!iv) return;
      const video = iv.querySelector('video');
      if (!video) return;
      iv.hidden = false;
      tile.querySelector(SELECTORS.thumb)?.style.setProperty('opacity', '0');
      video.play().catch(() => {});
      this._activeInlineTile = tile;
    }

    // Desktop hover version — shows native controls for desktop UX (seek, volume).
    // ONLY called from mouseenter, never from click/tap.
    _playInlineHover(tile) {
      this._pauseAllInline();
      const iv = tile.querySelector(SELECTORS.inlineVideo);
      if (!iv) return;
      const video = iv.querySelector('video');
      if (!video) return;
      video.controls = true;
      iv.hidden = false;
      tile.querySelector(SELECTORS.thumb)?.style.setProperty('opacity', '0');
      video.play().catch(() => {});
      this._activeInlineTile = tile;
    }

    _pauseAllInline() {
      this.el.querySelectorAll(SELECTORS.tile).forEach(tile => {
        const iv = tile.querySelector(SELECTORS.inlineVideo);
        if (!iv || iv.hidden) return;
        iv.querySelector('video')?.pause();
        iv.hidden = true;
        tile.querySelector(SELECTORS.thumb)?.style.removeProperty('opacity');
      });
      this._activeInlineTile = null;
    }

    // ── Dots ──────────────────────────────────────────────────────────────────

    _bindDots() {
      const tiles = Array.from(this.el.querySelectorAll(SELECTORS.tile));

      this.el.querySelectorAll(SELECTORS.dot).forEach((dot, i) => {
        dot.addEventListener('click', () => {
          const tile = tiles[i];
          if (!tile || !this.track) return;

          this._scrollToTile(tile);
          this._setActiveTile(tile);

          const isNative = tile.dataset.videoType === 'hosted' || tile.dataset.videoType === 'shopify';
          if (isNative && (this.hoverAutoplay || this.playbackMode === 'inline' || this.playbackMode === 'inline-unmute')) {
            setTimeout(() => this._playInline(tile), 350);
          }
        });
      });
    }

    // ── Modal ──────────────────────────────────────────────────────────────────

    _openModal(tile) {
      if (!this.modal) return;
      this._pauseAllInline();

      const type    = tile.dataset.videoType;
      const src     = tile.dataset.videoSrc;
      const ytId    = tile.dataset.youtubeId;
      const vmId    = tile.dataset.vimeoId;
      const content = this.modal.querySelector(SELECTORS.modalContent);
      content.innerHTML = '';

      let nativeVideo = null;

      if ((type === 'hosted' || type === 'shopify') && src) {
        const video = document.createElement('video');
        video.src = src;
        video.controls = true;
        // playsinline required on iOS to play within the modal instead of full-screen
        video.setAttribute('playsinline', '');
        video.style.cssText = 'width:100%;height:100%;object-fit:contain;background:#000;';
        content.appendChild(video);
        nativeVideo = video;
      } else if (type === 'youtube' && ytId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(ytId)}?autoplay=1&rel=0`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        content.appendChild(iframe);
      } else if (type === 'vimeo' && vmId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(vmId)}?autoplay=1`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        content.appendChild(iframe);
      } else { return; }

      // Show modal first (synchronously removes 'hidden') so the video is in a
      // visible container before play() is called — required for iOS.
      this._showModal();
      if (nativeVideo) {
        nativeVideo.play().catch(() => {});
      }
    }

    _showModal() {
      const modal = this.modal;
      this._prevFocus = document.activeElement;
      modal.removeAttribute('hidden');
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        modal.setAttribute('aria-modal', 'true');
        this._trapFocus(modal);
      });
    }

    _closeModal() {
      const modal = this.modal;
      if (!modal || !modal.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-modal', 'false');
      if (this._focusTrapHandler) {
        modal.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
      modal.addEventListener('transitionend', () => {
        modal.setAttribute('hidden', '');
        modal.querySelector(SELECTORS.modalContent).innerHTML = '';
        this._prevFocus?.focus();
        this._prevFocus = null;
      }, { once: true });
    }

    _bindModal() {
      if (!this.modal) return;
      this.modal.querySelector(SELECTORS.modalClose)?.addEventListener('click', () => this._closeModal());
      if (this.modal.dataset.closeOnOutside === 'true') {
        this.modal.addEventListener('click', e => {
          const inner = this.modal.querySelector(SELECTORS.modalInner);
          if (inner && !inner.contains(e.target)) this._closeModal();
        });
      }
      this._escHandler = e => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this._closeModal();
      };
      document.addEventListener('keydown', this._escHandler);
    }

    // ── Focus trap ─────────────────────────────────────────────────────────────

    _trapFocus(el) {
      const focusable = Array.from(el.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusable.length) return;
      focusable[0].focus();
      this._focusTrapHandler = e => {
        if (e.key !== 'Tab') return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      };
      el.addEventListener('keydown', this._focusTrapHandler);
    }

    // ── Scroll arrows ──────────────────────────────────────────────────────────

    _bindArrows() {
      const prev = this.el.querySelector(SELECTORS.prev);
      const next = this.el.querySelector(SELECTORS.next);
      if (!this.track || (!prev && !next)) return;

      const navigate = dir => {
        const tiles = Array.from(this.el.querySelectorAll(SELECTORS.tile));
        if (!tiles.length) return;

        const currentIdx = this._activeTile ? tiles.indexOf(this._activeTile) : 0;
        const nextIdx    = Math.max(0, Math.min(tiles.length - 1, currentIdx + dir));
        const nextTile   = tiles[nextIdx];
        if (!nextTile || nextTile === this._activeTile) return;

        this._scrollToTile(nextTile);
        this._setActiveTile(nextTile);

        const isNative = nextTile.dataset.videoType === 'hosted' || nextTile.dataset.videoType === 'shopify';
        if (isNative && (this.hoverAutoplay || this.playbackMode === 'inline' || this.playbackMode === 'inline-unmute')) {
          setTimeout(() => this._playInline(nextTile), 300);
        }
      };

      prev?.addEventListener('click', () => navigate(-1));
      next?.addEventListener('click', () => navigate(1));
    }

    // ── Shared scroll helper ───────────────────────────────────────────────────

    _scrollToTile(tile) {
      if (!this.track) return;
      const scrollLeft = tile.offsetLeft - (this.track.clientWidth - tile.offsetWidth) / 2;
      this.track.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
    }

    // ── IntersectionObserver autoplay ──────────────────────────────────────────

    _observeAutoplay() {
      if (this.playbackMode !== 'inline') return;
      this.el.querySelectorAll(`${SELECTORS.tile}[data-autoplay-visible="true"]`).forEach(tile => {
        if (tile.dataset.videoType !== 'hosted' && tile.dataset.videoType !== 'shopify') return;

        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (prefersReducedMotion && this.pauseForMotion) return;
              this._playInline(tile);
            } else {
              const iv = tile.querySelector(SELECTORS.inlineVideo);
              if (iv && !iv.hidden) {
                iv.querySelector('video')?.pause();
                iv.hidden = true;
                tile.querySelector(SELECTORS.thumb)?.style.removeProperty('opacity');
                if (this._activeInlineTile === tile) this._activeInlineTile = null;
              }
            }
          });
        }, { threshold: 0.5 });

        observer.observe(tile);
        this._observers.push(observer);
      });
    }

    // ── Destroy ────────────────────────────────────────────────────────────────

    destroy() {
      this._observers.forEach(obs => obs.disconnect());
      this._observers = [];
      this._pauseAllInline();
      if (this._scrollHandler && this.track) {
        this.track.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      if (this.track && this._drag) {
        const d = this._drag;
        this.track.removeEventListener('pointerdown', d.onDown);
        this.track.removeEventListener('click', d.onClick, true);
        this.track.removeEventListener('dragstart', d.onDragStart);
        window.removeEventListener('pointermove', d.onMove);
        window.removeEventListener('pointerup', d.onUp);
        this._drag = null;
      }
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
      if (this.modal) {
        this.modal.querySelector(SELECTORS.modalContent).innerHTML = '';
        this.modal.setAttribute('hidden', '');
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-modal', 'false');
      }
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new VideoGallery(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) {
        instances.get(el).destroy();
        instances.delete(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

})();
